/**
 * SopVersionBanner — Operator "what changed" banner (Phase 23.2.7).
 *
 * Shown above the SOP hierarchy on /operator/sops/:sopId when:
 *  - The SOP has a published version (sopDocuments.currentVersionId is set)
 *  - AND the operator has not yet acknowledged that specific version
 *
 * Visibility is server-driven (operator_sop_acknowledgments table) so dismissal
 * survives reloads and new devices. A subsequent version publish re-shows the
 * banner automatically because the acknowledged versionId no longer matches the
 * current one.
 *
 * Styling: amber (informational, not error). Uses role="status" + aria-live
 * so AT users hear the announcement when the banner mounts.
 */

import { useCallback } from 'react';
import { Sparkles, X } from 'lucide-react';
import {
  useSopVersionBanner,
  useAcknowledgeSopVersion,
} from '../../../hooks/useSopVersionBanner';

function formatPublishedDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return null;
  }
}

interface SopVersionBannerProps {
  sopId: string;
}

export function SopVersionBanner({ sopId }: SopVersionBannerProps) {
  const { data, isLoading } = useSopVersionBanner(sopId);
  const acknowledge = useAcknowledgeSopVersion(sopId);

  const handleDismiss = useCallback(() => {
    if (!data?.showBanner || !data.versionId) return;
    acknowledge.mutate({ versionId: data.versionId });
  }, [data, acknowledge]);

  const handleSeeChanges = useCallback(() => {
    if (!data?.firstChangedStepId) return;
    // Best-effort deep link. The current SopViewerPage doesn't assign ids to
    // step LIs, so in V1 this scrolls to the section containing the step if
    // the caller has set an id for it. Otherwise a no-op — the banner still
    // shows the changeSummary text.
    //
    // TODO(23.2.7-followup): add per-step ids on the SopViewerPage so this
    // scroll-into-view becomes a precise anchor.
    const el = document.getElementById(`step-${data.firstChangedStepId}`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [data]);

  // Loading or hidden: render nothing. We specifically DO NOT render a
  // skeleton — the banner is supplementary, and showing a placeholder for
  // 300ms then hiding it creates a layout-shift flicker.
  if (isLoading || !data?.showBanner) {
    return null;
  }

  const publishedLabel = formatPublishedDate(data.publishedAt);

  return (
    <div
      role="status"
      aria-live="polite"
      className="relative rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-amber-100">
          <Sparkles size={18} className="text-amber-600" strokeWidth={2} />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-amber-900">
            This SOP was updated
            {data.versionNumber ? (
              <>
                {' '}to version{' '}
                <span className="font-bold">v{data.versionNumber}</span>
              </>
            ) : null}
            {publishedLabel ? <> on {publishedLabel}</> : null}.
          </p>
          {data.changeSummary ? (
            <p className="mt-1 text-xs leading-relaxed text-amber-800">
              {data.changeSummary}
            </p>
          ) : null}

          {data.firstChangedStepId ? (
            <button
              type="button"
              onClick={handleSeeChanges}
              className="mt-2 inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-1 text-[11px] font-semibold text-amber-900 transition-colors hover:bg-amber-200"
            >
              See what changed
            </button>
          ) : null}
        </div>

        <button
          type="button"
          onClick={handleDismiss}
          disabled={acknowledge.isPending}
          aria-label="Dismiss update banner"
          className="flex-shrink-0 rounded-md p-1 text-amber-700 transition-colors hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <X size={16} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
